import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  getSortedRowModel,
  getPaginationRowModel,
  getFilteredRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  ColumnFiltersState
} from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
  Divider,
  Box
} from '@mui/material';
import { useState } from 'react';
import { observer } from 'mobx-react-lite';
import Pagination from '../shared/pagination';
import EmptyTable from '../../../icons/svg/empty-table.svg';
import { useTranslation } from 'react-i18next';
import { createBenefitSetColumns } from './columns';
import { globalFilterFn } from '../../../util/filter';
import { BenefitSetOverviewToolBar } from './toolbars/benefitSet-toolbar';
import { SortableTableHeader } from '../shared/sortable-header';
import { DEFAULT_PAGE_SIZE, scrollableTableContainerSx } from '../shared/utils';
import { useStore } from '../../../../hooks/useStore';
import { useListBenefitSet } from '../../../../queries/benefit/benefitSet';
import { Loading } from '../../atomic';

export const BenefitSetOverviewTable = observer(() => {
  const { t } = useTranslation();
  const { companyStore } = useStore();

  const { data: benefitSetData, isLoading } = useListBenefitSet(companyStore.company?.id ?? '');
  const [globalFilter, setGlobalFilter] = useState('');
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: DEFAULT_PAGE_SIZE
  });

  const columns = createBenefitSetColumns();

  const table = useReactTable({
    data: benefitSetData?.results ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    globalFilterFn: globalFilterFn,
    onGlobalFilterChange: setGlobalFilter,
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    onColumnFiltersChange: setColumnFilters,
    sortDescFirst: false,
    state: {
      pagination,
      globalFilter,
      columnFilters
    }
  });

  if (isLoading) {
    return (
      <Paper className="p-8">
        <Box className="flex justify-center items-center">
          <Loading />
        </Box>
      </Paper>
    );
  }

  return (
    <Paper className="overflow-hidden flex flex-col h-[calc(100vh-250px)]">
      <BenefitSetOverviewToolBar table={table} customHeadline={'benefitSet:benefitSet'} />
      <TableContainer sx={scrollableTableContainerSx('calc(100vh - 400px)')}>
        <Table>
          <TableHead>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <SortableTableHeader key={header.id} header={header} />
                ))}
              </TableRow>
            ))}
          </TableHead>
          <TableBody>
            {table.getRowModel().rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="!border-none">
                  <div className="flex flex-col items-center justify-center py-8 gap-y-2">
                    <img src={EmptyTable} className="w-64 h-32" alt="Empty Table" />
                    <div className="font-medium text-base">
                      {t('common:EMPTY_TABLE_TITLE_rows')}
                    </div>
                    <div className="text-grey-600 text-sm">
                      {t('benefitSet:createSetHelperText')}
                    </div>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell className="py-2 border-grey-100" key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Divider className="border-grey-100" />
      <Pagination table={table} />
    </Paper>
  );
});
